import type { DayOfWeek } from "@prisma/client";
import { createServerFn } from "@tanstack/react-start";

import type { DateYYYYMMDD } from "@/types/date";

import type { BookingFormDto } from "./actions";
import type { SubmitBookingFeedbackInput } from "./feedback";
import {
    cancelBookingByTokenServer,
    extractBookingIntentServer,
    fetchBookingByCancellationIdServer,
    fetchBookingByFeedbackTokenServer,
    fetchBookingCancellationReasonsServer,
    fetchBookingDeclineReasonsServer,
    fetchSiblingStoresServer,
    fetchStoreInfoServer,
    fetchTimesByDateServer,
    saveBookingByGuestServer,
    submitBookingFeedbackServer,
} from "./ops";

type BookingTimesInput = {
    storeId: string;
    date: DateYYYYMMDD;
    dayOfWeek: DayOfWeek;
    totalGuests: number;
    excludeBookingId?: string;
    bypassLeadTime?: boolean;
    durationMinutesOverride?: number;
};

export const getTimesByDate = createServerFn({ method: "GET" })
    .inputValidator((data: BookingTimesInput) => data)
    .handler(async ({ data }) => {
        return fetchTimesByDateServer(
            data.storeId,
            data.date,
            data.dayOfWeek,
            data.totalGuests,
            data.excludeBookingId,
            data.bypassLeadTime,
            data.durationMinutesOverride,
        );
    });

type StoreInfoInput = {
    storeId: string;
};

export const getStoreInfoByIdFn = createServerFn({ method: "GET" })
    .inputValidator((data: StoreInfoInput) => data)
    .handler(async ({ data }) => {
        return fetchStoreInfoServer(data.storeId);
    });

export const getSiblingStoresFn = createServerFn({ method: "GET" })
    .inputValidator((data: StoreInfoInput) => data)
    .handler(async ({ data }) => {
        return fetchSiblingStoresServer(data.storeId);
    });

type BookingByCancellationIdInput = {
    token: string;
};

export const getBookingByCancellationIdFn = createServerFn({ method: "GET" })
    .inputValidator((data: BookingByCancellationIdInput) => data)
    .handler(async ({ data }) => {
        return fetchBookingByCancellationIdServer(data.token);
    });

export const getBookingCancellationReasonsFn = createServerFn({ method: "GET" }).handler(
    async () => {
        return fetchBookingCancellationReasonsServer();
    },
);

export const getBookingDeclineReasonsFn = createServerFn({ method: "GET" }).handler(async () => {
    return fetchBookingDeclineReasonsServer();
});

type ExtractBookingIntentInput = {
    text: string;
    today: string;
    token: string;
};

export const extractBookingIntentFn = createServerFn({ method: "POST" })
    .inputValidator((data: ExtractBookingIntentInput) => data)
    .handler(async ({ data }) => {
        return extractBookingIntentServer(data.text, data.today, data.token);
    });

type SaveBookingByGuestInput = {
    payload: BookingFormDto;
    captchaToken: string;
};

export const saveBookingByGuestFn = createServerFn({ method: "POST" })
    .inputValidator((data: SaveBookingByGuestInput) => data)
    .handler(async ({ data }) => {
        return saveBookingByGuestServer(data.payload, data.captchaToken);
    });

type CancelBookingByTokenInput = {
    token: string;
    reasonCode: string;
};

export const cancelBookingByTokenFn = createServerFn({ method: "POST" })
    .inputValidator((data: CancelBookingByTokenInput) => data)
    .handler(async ({ data }) => {
        return cancelBookingByTokenServer(data.token, data.reasonCode);
    });

type BookingByFeedbackTokenInput = {
    token: string;
};

export const getBookingByFeedbackTokenFn = createServerFn({ method: "GET" })
    .inputValidator((data: BookingByFeedbackTokenInput) => data)
    .handler(async ({ data }) => {
        return fetchBookingByFeedbackTokenServer(data.token);
    });

export const submitBookingFeedbackFn = createServerFn({ method: "POST" })
    .inputValidator((data: SubmitBookingFeedbackInput) => data)
    .handler(async ({ data }) => {
        return submitBookingFeedbackServer(data);
    });
